import * as React from "react";
import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Checkbox from "@mui/material/Checkbox";
import { FormInputProps } from "../../models/form.model";
import { Controller } from "react-hook-form";
import { concat, pull } from "lodash";
import { Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import Tooltip from "@mui/material/Tooltip";

export interface CheckBoxOption {
  label: string;
  value: string;
  unique?: boolean;
}

export interface CheckBoxGroup {
  options: CheckBoxOption[];
  label?: string;
}

export default function FormCheckboxGroup(
  props: FormInputProps & { groups: CheckBoxGroup[]; dataTestId?: string }
) {
  const {
    name,
    control,
    label,
    required,
    groups,
    helperText,
    setError,
    rules,
    dataTestId,
  } = props;
  const setErrorMsg = (message: string) => {
    setError(name, { type: "manual", message });
  };
  const [uniqueOption, setUniqueOption] = useState<
    (CheckBoxOption & { checked: boolean }) | undefined
  >();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, value = [] },
        fieldState: { error },
        formState,
      }) => {
        return (
          <Box data-testid={dataTestId || name}>
            <FormLabel component="legend" required={required} error={!!error}>
              {label}
            </FormLabel>
            <FormControl
              required={required}
              error={!!error}
              component="fieldset"
              variant="standard"
            >
              {groups.map((group, i) => (
                <div key={`group_${i}`}>
                  <Typography variant="caption">{group.label}</Typography>
                  <FormGroup sx={{ gap: 2 }}>
                    {group.options.map((option, i) => {
                      const hasTextbox =
                        typeof option.label === "string" &&
                        ~option.label.indexOf("{{textbox}}");
                      let label = option.label;
                      if (hasTextbox) {
                        label = label.replace(/{{textbox}}/g, "");
                      }
                      const disabled =
                        uniqueOption?.checked &&
                        uniqueOption.value !== option.value;
                      return (
                        <Tooltip
                          title={
                            disabled
                              ? `Please uncheck the "${uniqueOption?.label}" option for selecting`
                              : undefined
                          }
                          placement={"bottom-start"}
                        >
                          <div>
                            <FormControlLabel
                              key={option.value}
                              data-testid={`${dataTestId || name}-option${i}`}
                              control={
                                <Checkbox
                                  checked={
                                    !!(value || []).find(
                                      (a: any) =>
                                        `${a}` === `${option.value}` ||
                                        a.indexOf(`${option.value}#:!`) === 0
                                    )
                                  }
                                  disabled={disabled}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (option.unique) {
                                        onChange([e.target.name]);
                                        setUniqueOption({
                                          ...option,
                                          checked: true,
                                          label,
                                        });
                                      } else {
                                        onChange(
                                          concat([...value], e.target.name)
                                        );
                                      }
                                    } else {
                                      onChange(
                                        [...value].filter(
                                          (a) => a.indexOf(e.target.name) < 0
                                        )
                                      );
                                      if (option.unique) {
                                        setUniqueOption(undefined);
                                      }
                                    }
                                  }}
                                  name={option.value}
                                />
                              }
                              label={
                                hasTextbox ? (
                                  <Stack direction="column" spacing={2}>
                                    <Typography>{label}</Typography>
                                    {(value || []).find(
                                      (a: any) =>
                                        a.indexOf(`${option.value}`) === 0
                                    ) && (
                                        <TextField
                                          required
                                          fullWidth
                                          multiline
                                          rows={3}
                                          label={label}
                                          variant="outlined"
                                          onChange={(e) => {
                                            const data = [...value].filter(
                                              (a) => a.indexOf(option.value) < 0
                                            );
                                            data.push(
                                              `${option.value}#:!${e.target.value}`
                                            );
                                            onChange(data);
                                          }}
                                        />
                                      )}
                                  </Stack>
                                ) : (
                                  label
                                )
                              }
                            />
                          </div>
                        </Tooltip>
                      );
                    })}
                  </FormGroup>
                </div>
              ))}

              <FormHelperText error={!!error}>
                {error ? error.message : helperText || null}
              </FormHelperText>
            </FormControl>
          </Box>
        );
      }}
    />
  );
}
